/**
 * Color conversion utilities for light positioning in color circles
 * Handles HSV, CIE XY, and Color Temperature conversions
 */

export interface HSV {
  h: number // 0-360
  s: number // 0-100
  v: number // 0-100
}

export interface HueState {
  hue: number // 0-65535
  saturation: number // 0-254
  brightness: number // 1-254
}

export interface XYCoord {
  x: number // -1 to 1 (circle coordinates)
  y: number // -1 to 1 (circle coordinates)
}

export interface ColorTemperature {
  ct: number // 153-500 mireds
  brightness: number // 1-254
}

/**
 * Convert Hue bridge hue/sat values to HSV
 */
export function hueStateToHSV(hue: number, saturation: number): HSV {
  return {
    h: (hue / 65535) * 360,
    s: (saturation / 254) * 100,
    v: 100 // We handle brightness separately
  }
}

/**
 * Convert HSV to Hue bridge hue/sat values
 */
export function hsvToHueState(hsv: HSV): { hue: number; saturation: number } {
  return {
    hue: Math.round((hsv.h / 360) * 65535),
    saturation: Math.round((hsv.s / 100) * 254)
  }
}

/**
 * Convert HSV to XY coordinates for color circle positioning
 * Maps hue to angle, saturation to radius
 */
export function hsvToXY(hsv: HSV): XYCoord {
  const angle = (hsv.h * Math.PI) / 180
  const radius = hsv.s / 100
  
  return {
    x: radius * Math.cos(angle),
    y: radius * Math.sin(angle)
  }
}

/**
 * Convert XY circle coordinates to HSV
 */
export function xyToHSV(coord: XYCoord): HSV {
  const radius = Math.sqrt(coord.x * coord.x + coord.y * coord.y)
  const angle = Math.atan2(coord.y, coord.x)
  
  let hue = (angle * 180) / Math.PI
  if (hue < 0) hue += 360
  
  return {
    h: hue,
    s: Math.min(radius * 100, 100),
    v: 100
  }
}

/**
 * Convert color temperature to XY coordinate for white balance circle
 * Maps 153 (cold) to -1 (left) and 500 (warm) to 1 (right)
 */
export function ctToXY(ct: number): XYCoord {
  // Normalize to -1 to 1 range
  const normalized = ((ct - 153) / (500 - 153)) * 2 - 1
  
  return {
    x: normalized,
    y: 0 // White balance is horizontal only
  }
}

/**
 * Convert XY coordinate to color temperature
 * Only uses X coordinate (horizontal position)
 */
export function xyToCT(coord: XYCoord): number {
  // Clamp x to -1 to 1
  const clampedX = Math.max(-1, Math.min(1, coord.x))
  
  // Map -1 to 1 to 153 to 500
  const ct = ((clampedX + 1) / 2) * (500 - 153) + 153
  
  return Math.round(ct)
}

/**
 * Convert RGB to HSV (for gamut calculations if needed)
 */
export function rgbToHSV(r: number, g: number, b: number): HSV {
  r /= 255
  g /= 255
  b /= 255
  
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  
  let h = 0
  const s = max === 0 ? 0 : delta / max
  const v = max
  
  if (delta !== 0) {
    if (max === r) {
      h = ((g - b) / delta + (g < b ? 6 : 0)) / 6
    } else if (max === g) {
      h = ((b - r) / delta + 2) / 6
    } else {
      h = ((r - g) / delta + 4) / 6
    }
  }
  
  return {
    h: h * 360,
    s: s * 100,
    v: v * 100
  }
}

/**
 * Convert HSV to RGB
 */
export function hsvToRGB(hsv: HSV): { r: number; g: number; b: number } {
  const h = hsv.h
  const s = hsv.s / 100
  const v = hsv.v / 100
  
  const c = v * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - c
  
  let r = 0, g = 0, b = 0
  
  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c
  } else {
    r = c; g = 0; b = x
  }
  
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  }
}

/**
 * Convert color temperature (mireds) to RGB (approximation)
 */
export function ctToRGB(ct: number): { r: number; g: number; b: number } {
  // Convert mireds to Kelvin
  const kelvin = 1000000 / ct
  
  let r, g, b
  
  // Red
  if (kelvin <= 6600) {
    r = 255
  } else {
    r = 329.698727446 * Math.pow(kelvin / 100 - 60, -0.1332047592)
    r = Math.max(0, Math.min(255, r))
  }
  
  // Green
  if (kelvin <= 6600) {
    g = 99.4708025861 * Math.log(kelvin / 100) - 161.1195681661
  } else {
    g = 288.1221695283 * Math.pow(kelvin / 100 - 60, -0.0755148492)
  }
  g = Math.max(0, Math.min(255, g))
  
  // Blue
  if (kelvin >= 6600) {
    b = 255
  } else if (kelvin <= 1900) {
    b = 0
  } else {
    b = 138.5177312231 * Math.log(kelvin / 100 - 10) - 305.0447927307
    b = Math.max(0, Math.min(255, b))
  }
  
  return {
    r: Math.round(r),
    g: Math.round(g),
    b: Math.round(b)
  }
}

/**
 * Check if a position is within circle bounds
 */
export function isWithinCircle(coord: XYCoord): boolean {
  const distance = Math.sqrt(coord.x * coord.x + coord.y * coord.y)
  return distance <= 1
}

/**
 * Clamp coordinates to circle bounds
 */
export function clampToCircle(coord: XYCoord): XYCoord {
  const distance = Math.sqrt(coord.x * coord.x + coord.y * coord.y)
  
  if (distance <= 1) {
    return coord
  }
  
  return {
    x: coord.x / distance,
    y: coord.y / distance
  }
}

/**
 * Get light position based on current state
 */
export function getLightPosition(light: {
  current_state: {
    on?: boolean
    hue?: number
    sat?: number
    saturation?: number
    ct?: number
    color_temperature?: number
    bri?: number
    brightness?: number
  }
  capabilities?: Record<string, unknown>
}): { hue?: XYCoord; white?: XYCoord } {
  const result: { hue?: XYCoord; white?: XYCoord } = {}
  
  const state = light.current_state
  const capabilities = light.capabilities || {}
  
  // Get saturation value (handle both property names)
  const saturation = state.sat ?? state.saturation
  
  // Get color temperature (handle both property names)
  const colorTemp = state.ct ?? state.color_temperature
  
  // Check if light supports color
  const control = capabilities?.control as any
  const supportsColor = control?.colorgamut || 
                        control?.colorgamuttype ||
                        state.hue !== undefined
  
  // Check if light supports color temperature
  const supportsColorTemp = control?.ct || 
                           colorTemp !== undefined
  
  // Get hue position if available and saturation is > 0
  // If saturation is 0, the light is in white mode, not color mode
  // Show in color circle if light has hue/sat and NO ct (color mode)
  if (supportsColor && state.hue !== undefined && saturation !== undefined && saturation > 10 && colorTemp === undefined) {
    const hsv = hueStateToHSV(state.hue, saturation)
    result.hue = hsvToXY(hsv)
  }
  
  // Get white balance position if available
  // Show in white circle if light has ct (white mode)
  if (supportsColorTemp && colorTemp !== undefined) {
    result.white = ctToXY(colorTemp)
  }
  
  // If no position was set but light supports color, default to middle of hue circle
  if (!result.hue && !result.white && supportsColor) {
    result.hue = { x: 0, y: 0 }
  }
  
  // If no position was set but light supports color temp, default to neutral white
  if (!result.white && supportsColorTemp) {
    result.white = { x: 0, y: 0 }
  }
  
  return result
}
